// GuildRecruitingPanel.tsx
// Officers say what kind of guild this is (styles: Casual, PvP, ...), who
// the guild is looking for, per game, and link the guild's logs page. What they pick is what recruits see on the directory card
// ("Looking for") and in the sidebar's "Most wanted".
//
// Picking works like the Alignment sign up form: a row of class icons, then
// that class's specs. Each pick can be marked high priority. The stats rollup
// may suggest a role the active roster is short of for the current raid; that
// is shown as a hint, never picked for them.
//
// Server: PUT /guilds/:id/games/:gameId/needs and .../logs
// (auth/guild_game_routes.js). Reads GET /guilds/:id/stats for the current
// needs, suggestions and logs link. Owner or officer only.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { BODY, DISPLAY, Icon, tc, useAlignmentTheme } from "./alignmentUi";
import { shortGameName } from "./lib/gameName";
import { StylePill } from "./GuildDirectory";
import SpecPicker from "./SpecPicker";
import type { GuildGameStats, GuildStyle } from "./GuildDirectory";

type Opt = { id: number; key: string; label: string; metadata?: any; image_url?: string | null };
type Group = { key: string; options: Opt[] };
type Pick = { specId: number; priority: "high" | "normal" };

const ROLE_WORD: Record<string, string> = { tank: "tanks", healer: "healers" };
const ERRORS: Record<string, string> = {
  too_many_styles: "Pick at most 4 styles.",
  unknown_style: "One of those styles no longer exists. Reload and pick again.",
  too_many_needs: "Pick at most 8. Recruits skim; a short list reads as a real need.",
  unknown_spec: "One of those specs no longer exists. Reload and pick again.",
  invalid_logs_url: "That is not a guild page on this game's logs site.",
  game_has_no_logs_site: "This game has no logs site set up yet.",
  forbidden: "Only the owner or an officer can change this.",
};

export default function GuildRecruitingPanel({ guildId, recruiting }: { guildId: string; recruiting: boolean }) {
  useAlignmentTheme();
  const [canManage, setCanManage] = useState(false);
  const [stats, setStats] = useState<GuildGameStats[]>([]);
  const [gameId, setGameId] = useState<string>("");
  const [fields, setFields] = useState<Group[]>([]);
  const [picks, setPicks] = useState<Pick[]>([]);
  const [styleIds, setStyleIds] = useState<number[]>([]);
  const [styleOptions, setStyleOptions] = useState<Record<string, GuildStyle[]>>({});
  const [logsUrl, setLogsUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const load = useCallback(async () => {
    try {
      const [gRes, sRes] = await Promise.all([
        fetch(`/guilds/${guildId}/games`, { credentials: "include" }),
        fetch(`/guilds/${guildId}/stats`, { credentials: "include" }),
      ]);
      if (gRes.ok) setCanManage(!!(await gRes.json()).canManage);
      if (sRes.ok) {
        const games: GuildGameStats[] = (await sRes.json()).games || [];
        setStats(games);
        setGameId((cur) => (cur && games.some((g) => g.gameId === cur) ? cur : games[0]?.gameId || ""));
      }
    } catch { /* panel stays hidden */ }
  }, [guildId]);
  useEffect(() => { load(); }, [load]);

  // Each game's style list (game config), from GET /games.
  useEffect(() => {
    fetch("/games", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then((rows: Array<{ id: string; guildStyles?: GuildStyle[] }>) =>
        setStyleOptions(Object.fromEntries(rows.map((g) => [g.id, g.guildStyles || []]))))
      .catch(() => setStyleOptions({}));
  }, []);

  const game = stats.find((g) => g.gameId === gameId) || null;

  // Reset the editor to what is saved whenever the game changes.
  useEffect(() => {
    setPicks((game?.needs || []).map((n) => ({ specId: n.specId, priority: n.priority })));
    setStyleIds((game?.styles || []).map((st) => st.id));
    setLogsUrl(game?.logsUrl || "");
    setMsg(null);
  }, [game]);

  useEffect(() => {
    setFields([]);
    if (!gameId) return;
    fetch(`/games/${encodeURIComponent(gameId)}/fields`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then(setFields)
      .catch(() => setFields([]));
  }, [gameId]);

  const classes = useMemo(() => fields.find((g) => g.key === "class")?.options || [], [fields]);
  const specs = useMemo(() => fields.find((g) => g.key === "spec")?.options || [], [fields]);
  const specById = useMemo(() => new Map(specs.map((s) => [s.id, s])), [specs]);
  const classOf = (s: Opt | undefined) => classes.find((c) => c.key === s?.metadata?.class_key);

  if (!canManage || !game) return null;

  const saved = JSON.stringify((game.needs || []).map((n) => ({ specId: n.specId, priority: n.priority })));
  const savedStyles = JSON.stringify((game.styles || []).map((st) => st.id).sort((a, b) => a - b));
  const stylesDirty = JSON.stringify([...styleIds].sort((a, b) => a - b)) !== savedStyles;
  const dirty = JSON.stringify(picks) !== saved || stylesDirty || logsUrl.trim() !== (game.logsUrl || "");
  const gameStyles = styleOptions[game.gameId] || [];
  const toggleStyle = (id: number) => setStyleIds((cur) =>
    cur.includes(id) ? cur.filter((x) => x !== id) : cur.length >= 4 ? cur : [...cur, id]
  );

  const toggle = (specId: number) => setPicks((p) =>
    p.some((x) => x.specId === specId) ? p.filter((x) => x.specId !== specId) : [...p, { specId, priority: "normal" }]
  );
  const flip = (specId: number) => setPicks((p) =>
    p.map((x) => (x.specId === specId ? { ...x, priority: x.priority === "high" ? "normal" : "high" } : x))
  );

  const put = async (path: string, body: any) => {
    const r = await fetch(`/guilds/${guildId}/games/${gameId}/${path}`, {
      method: "PUT", credentials: "include",
      headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(ERRORS[j.error] || j.error || "Something went wrong");
  };

  const save = async () => {
    setBusy(true); setMsg(null);
    try {
      if (stylesDirty) await put("styles", { styleIds });
      if (JSON.stringify(picks) !== saved) await put("needs", { needs: picks });
      if (logsUrl.trim() !== (game.logsUrl || "")) await put("logs", { logsUrl: logsUrl.trim() || null });
      await load();
      setMsg({ ok: true, text: "Saved. Your directory card shows it now." });
    } catch (e: any) {
      setMsg({ ok: false, text: e.message });
    } finally {
      setBusy(false);
    }
  };


  return (
    <section style={BODY} className="rounded-3xl border border-[var(--c-line)] bg-[color-mix(in_srgb,var(--c-surface)_85%,transparent)] p-5 shadow-[var(--c-shadow)] sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-xl text-[var(--c-ink)]" style={DISPLAY}>Recruiting</h3>
          <p className="mt-0.5 text-[14px] text-[var(--c-ink-3)]">
            {recruiting
              ? "What kind of guild you are and who you are looking for. Shown on your directory card."
              : "Recruiting is off in guild settings, so the directory shows you as not recruiting."}
          </p>
        </div>
        {stats.length > 1 && (
          <div className="inline-flex rounded-xl border border-[var(--c-line)] bg-[var(--c-sunken)] p-1">
            {stats.map((g) => (
              <button
                key={g.gameId}
                onClick={() => setGameId(g.gameId)}
                className={`rounded-lg px-3 py-1.5 text-[13px] font-semibold transition ${g.gameId === gameId ? "bg-[var(--c-surface)] text-[var(--c-ink)] shadow-[var(--c-shadow)]" : "text-[var(--c-ink-3)] hover:text-[var(--c-ink)]"}`}
              >
                {shortGameName(g.gameName)}
              </button>
            ))}
          </div>
        )}
      </div>

      {gameStyles.length > 0 && (
        <div className="mt-5">
          <div className="mb-2 flex items-baseline justify-between">
            <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-[var(--c-ink-3)]">What kind of guild</span>
            <span className="text-[11px] text-[var(--c-ink-4)]">{styleIds.length} of 4</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {gameStyles.map((st) => (
              <StylePill key={st.id} style={st} active={styleIds.includes(st.id)} onClick={() => toggleStyle(st.id)} />
            ))}
          </div>
        </div>
      )}

      {game.suggestedRoles.length > 0 && (
        <div className="mt-4 rounded-2xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-[13px] text-[var(--c-ink-2)]">
          {game.suggestedRoles.map((s) => (
            <div key={s.role}>
              Your active roster has <b>{s.have}</b> {ROLE_WORD[s.role] || s.role}; {s.raid} usually wants <b>{s.want}</b>.
            </div>
          ))}
        </div>
      )}

      {/* Picked */}
      <div className="mt-5">
        <div className="mb-2 flex items-baseline justify-between">
          <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-[var(--c-ink-3)]">Looking for</span>
          <span className="text-[11px] text-[var(--c-ink-4)]">{picks.length} of 8 · tap the star for high priority</span>
        </div>
        {picks.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-[var(--c-line)] px-4 py-3 text-[13px] text-[var(--c-ink-4)]">
            Nobody picked, so recruits see "open to every role". Pick a class below.
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {picks.map((p) => {
              const s = specById.get(p.specId);
              const c = classOf(s);
              if (!s) return null;
              return (
                <span key={p.specId} className="flex items-center gap-2 rounded-xl border border-[var(--c-line)] bg-[var(--c-surface)] py-1 pl-1 pr-1.5">
                  <Icon src={s.image_url} alt={s.label} size={28} ring={p.priority === "high" ? "#f59e0b" : null} />
                  <span className="text-[13px] font-semibold" style={{ color: tc(c?.metadata?.color || undefined) }}>{s.label} {c?.label}</span>
                  <button
                    onClick={() => flip(p.specId)}
                    title={p.priority === "high" ? "High priority (tap for normal)" : "Normal priority (tap for high)"}
                    className={`rounded-md px-1 text-[15px] leading-none ${p.priority === "high" ? "text-amber-500" : "text-[var(--c-ink-4)] hover:text-amber-500"}`}
                  >
                    {p.priority === "high" ? "★" : "☆"}
                  </button>
                  <button onClick={() => toggle(p.specId)} aria-label={`Remove ${s.label}`} className="rounded-md px-1 text-[15px] leading-none text-[var(--c-ink-4)] hover:text-[var(--c-ink)]">×</button>
                </span>
              );
            })}
          </div>
        )}
      </div>

      {/* Class, then spec */}
      <SpecPicker className="mt-5" classes={classes} specs={specs} selected={picks.map((p) => p.specId)} onToggle={toggle} />

      {/* Logs link */}
      <div className="mt-6">
        <label className="text-[11px] font-bold uppercase tracking-[0.18em] text-[var(--c-ink-3)]">
          {game.logsSite ? `${game.logsSite.name} guild page` : "Logs"}
        </label>
        {game.logsSite ? (
          <input
            value={logsUrl}
            onChange={(e) => setLogsUrl(e.target.value)}
            placeholder={`${game.logsSite.origin}/guild/...`}
            className="mt-1.5 w-full rounded-xl border border-[var(--c-line)] bg-[var(--c-surface)] px-3.5 py-2.5 text-[14px] text-[var(--c-ink)] outline-none transition focus:border-[var(--c-accent)]"
          />
        ) : (
          <p className="mt-1 text-[13px] text-[var(--c-ink-4)]">{shortGameName(game.gameName)} has no logs site set up yet.</p>
        )}
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <button
          onClick={save}
          disabled={!dirty || busy}
          className="rounded-xl bg-[var(--c-accent)] px-5 py-2.5 text-[14px] font-bold text-[var(--c-ink-on-fill)] transition hover:bg-[var(--c-accent-hover)] disabled:cursor-not-allowed disabled:bg-[var(--c-sunken-2)] disabled:text-[var(--c-ink-3)] dark:bg-gradient-to-b dark:from-amber-300 dark:to-amber-500 dark:disabled:bg-none"
        >
          {busy ? "Saving" : "Save"}
        </button>
        {msg && <span className={`text-[13px] ${msg.ok ? "text-emerald-700 dark:text-emerald-400" : "text-[var(--c-bad-ink)]"}`}>{msg.text}</span>}
      </div>
    </section>
  );
}
